import { useState } from 'react';
import { ScanSearch, ShieldAlert, AlertTriangle, ClipboardList, Lightbulb } from 'lucide-react';
import { runLogAnalysis, type SiemAnalysisResult, type ThreatFinding } from '../../hooks/useSecOps';

type AnalysisType = 'threat_hunt' | 'incident_investigation' | 'compliance_review' | 'anomaly_detection';
type TimePreset = '24h' | '7d' | '30d' | 'custom';

const ANALYSIS_TYPES: { value: AnalysisType; label: string }[] = [
  { value: 'threat_hunt', label: 'Threat Hunt' },
  { value: 'incident_investigation', label: 'Incident Investigation' },
  { value: 'compliance_review', label: 'Compliance Review' },
  { value: 'anomaly_detection', label: 'Anomaly Detection' },
];

const SEVERITY_COLORS: Record<string, string> = {
  critical: 'bg-red-100 text-red-700 border-red-200',
  high:     'bg-orange-100 text-orange-700 border-orange-200',
  medium:   'bg-yellow-100 text-yellow-700 border-yellow-200',
  low:      'bg-blue-100 text-blue-700 border-blue-200',
};

function getPresetTimes(preset: TimePreset): { start: string; end: string } | null {
  if (preset === 'custom') return null;
  const end = new Date();
  const start = new Date(end);
  if (preset === '24h') start.setHours(start.getHours() - 24);
  if (preset === '7d')  start.setDate(start.getDate() - 7);
  if (preset === '30d') start.setDate(start.getDate() - 30);
  return {
    start: start.toISOString().slice(0, 16),
    end:   end.toISOString().slice(0, 16),
  };
}

function ThreatCard({ threat }: { threat: ThreatFinding }) {
  return (
    <div className={`border rounded-lg p-4 ${SEVERITY_COLORS[threat.severity] ?? 'bg-gray-50 text-gray-700 border-gray-200'}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-semibold text-sm capitalize">{threat.type}</span>
            {threat.mitre_technique && (
              <span className="text-xs font-mono bg-black/10 px-1.5 py-0.5 rounded">{threat.mitre_technique}</span>
            )}
          </div>
          <p className="text-sm">{threat.description}</p>
          <div className="mt-2 flex flex-wrap gap-3 text-xs">
            <span>Devices: {threat.affected_devices.join(', ') || 'unknown'}</span>
            <span>Events: {threat.event_count}</span>
            <span>First: {new Date(threat.first_seen).toLocaleString()}</span>
          </div>
          <p className="mt-2 text-sm font-medium">Action: {threat.recommended_action}</p>
        </div>
        <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold capitalize border ${SEVERITY_COLORS[threat.severity] ?? ''}`}>
          {threat.severity}
        </span>
      </div>
    </div>
  );
}

export default function LogAnalysisPage() {
  const [clientId, setClientId]         = useState('');
  const [analysisType, setAnalysisType] = useState<AnalysisType>('threat_hunt');
  const [timePreset, setTimePreset]     = useState<TimePreset>('7d');
  const [customStart, setCustomStart]   = useState('');
  const [customEnd, setCustomEnd]       = useState('');
  const [incidentContext, setIncidentContext] = useState('');
  const [analyzing, setAnalyzing]       = useState(false);
  const [result, setResult]             = useState<SiemAnalysisResult | null>(null);
  const [error, setError]               = useState<string | null>(null);

  async function handleAnalyze() {
    if (!clientId.trim()) { setError('Client ID is required'); return; }

    const times = getPresetTimes(timePreset);
    const startTime = times ? times.start : customStart;
    const endTime   = times ? times.end   : customEnd;

    if (!startTime || !endTime) { setError('Select a time range'); return; }

    setAnalyzing(true);
    setError(null);
    setResult(null);

    try {
      const res = await runLogAnalysis({
        clientId: clientId.trim(),
        startTime: new Date(startTime).toISOString(),
        endTime:   new Date(endTime).toISOString(),
        analysisType,
        incidentContext: incidentContext.trim() || undefined,
      });
      setResult(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  }

  return (
    <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <ScanSearch className="w-6 h-6 text-indigo-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Full Log Analysis</h1>
          <p className="text-sm text-gray-500">THEO Scout analyzes the full log dataset in a single pass — no chunking, no missed patterns.</p>
        </div>
      </div>

      {/* Configuration */}
      <div className="bg-white rounded-lg shadow p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Client ID *</label>
            <input
              type="text"
              value={clientId}
              onChange={e => setClientId(e.target.value)}
              placeholder="client-uuid or client-slug"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Analysis Type</label>
            <select
              value={analysisType}
              onChange={e => setAnalysisType(e.target.value as AnalysisType)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              {ANALYSIS_TYPES.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Time Range</label>
          <div className="flex flex-wrap gap-2">
            {(['24h', '7d', '30d', 'custom'] as TimePreset[]).map(p => (
              <button
                key={p}
                onClick={() => setTimePreset(p)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium border transition-colors ${
                  timePreset === p
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-indigo-400'
                }`}
              >
                {p === '24h' ? 'Last 24h' : p === '7d' ? 'Last 7 days' : p === '30d' ? 'Last 30 days' : 'Custom'}
              </button>
            ))}
          </div>
          {timePreset === 'custom' && (
            <div className="mt-3 grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Start</label>
                <input type="datetime-local" value={customStart} onChange={e => setCustomStart(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">End</label>
                <input type="datetime-local" value={customEnd} onChange={e => setCustomEnd(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
              </div>
            </div>
          )}
        </div>

        {analysisType === 'incident_investigation' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Investigation Context (optional)</label>
            <textarea
              value={incidentContext}
              onChange={e => setIncidentContext(e.target.value)}
              rows={2}
              placeholder="Describe the incident or anomaly to focus the analysis..."
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">{error}</div>
        )}

        <button
          onClick={handleAnalyze}
          disabled={analyzing}
          className="px-5 py-2.5 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {analyzing ? (
            <>
              <span className="inline-block w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              Analyzing logs — this may take up to 90 seconds...
            </>
          ) : (
            <>
              <ScanSearch className="w-4 h-4" />
              Run Full Log Analysis
            </>
          )}
        </button>
      </div>

      {/* Results */}
      {result && (
        <div className="space-y-4">
          {/* Summary header */}
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <ShieldAlert className="w-5 h-5 text-indigo-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium text-indigo-900">{result.summary}</p>
                <p className="text-xs text-indigo-600 mt-1">
                  {result.log_volume_analyzed.toLocaleString()} log lines analyzed ·{' '}
                  {new Date(result.time_range_analyzed.start).toLocaleDateString()} –{' '}
                  {new Date(result.time_range_analyzed.end).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>

          {/* Threats */}
          {result.threats.length > 0 && (
            <div>
              <h2 className="flex items-center gap-2 text-base font-semibold text-gray-900 mb-3">
                <ShieldAlert className="w-4 h-4 text-red-500" />
                Threats ({result.threats.length})
              </h2>
              <div className="space-y-3">
                {result.threats.map((t, i) => <ThreatCard key={i} threat={t} />)}
              </div>
            </div>
          )}

          {/* Anomalies */}
          {result.anomalies.length > 0 && (
            <div>
              <h2 className="flex items-center gap-2 text-base font-semibold text-gray-900 mb-3">
                <AlertTriangle className="w-4 h-4 text-yellow-500" />
                Anomalies ({result.anomalies.length})
              </h2>
              <div className="bg-white rounded-lg shadow divide-y divide-gray-100">
                {result.anomalies.map((a, i) => (
                  <div key={i} className="p-4">
                    <p className="text-sm font-medium text-gray-900">{a.description}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Device: {a.affected_device} · {a.baseline_deviation} · First: {new Date(a.first_occurrence).toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Compliance gaps */}
          {result.compliance_gaps.length > 0 && (
            <div>
              <h2 className="flex items-center gap-2 text-base font-semibold text-gray-900 mb-3">
                <ClipboardList className="w-4 h-4 text-orange-500" />
                Compliance Gaps ({result.compliance_gaps.length})
              </h2>
              <div className="bg-white rounded-lg shadow divide-y divide-gray-100">
                {result.compliance_gaps.map((g, i) => (
                  <div key={i} className="px-4 py-3 text-sm text-gray-700">{g}</div>
                ))}
              </div>
            </div>
          )}

          {/* Recommendations */}
          {result.recommendations.length > 0 && (
            <div>
              <h2 className="flex items-center gap-2 text-base font-semibold text-gray-900 mb-3">
                <Lightbulb className="w-4 h-4 text-green-500" />
                Recommendations
              </h2>
              <div className="bg-white rounded-lg shadow divide-y divide-gray-100">
                {result.recommendations.map((r, i) => (
                  <div key={i} className="px-4 py-3 flex items-start gap-2">
                    <span className="text-xs font-bold text-gray-400 mt-0.5 w-4 shrink-0">{i + 1}.</span>
                    <p className="text-sm text-gray-700">{r}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {result.threats.length === 0 && result.anomalies.length === 0 && result.compliance_gaps.length === 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm text-green-700">
              No threats or anomalies detected in the analyzed log set.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
